<script lang="ts" setup>
import { CalculatedFieldConditionType } from '@/core/collections/contracts-api/calcuclated-field-condition-types/calculated-field-condition-type.interface';

import { ContractTemplateFieldName } from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import {
   ContractTemplateCalculatedValue,
   ContractTemplateCondition,
   ContractTemplateField,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { SalaryIndexationLog } from '@/core/collections/contracts-api/employers/salary-indexations/salary-indexation.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import FormElement from '@/core/components/form-elements/FormElement.vue';
import { useStore } from '@/core/hooks/useStore';
import { FormType } from '@/core/models/form-type.enum';
import { NxtInput, NxtLink } from '@nxtpeople/nxt-styles';
import { PropType, computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContractTemplateKeys } from './useContractTemplateKeys';

const { t } = useI18n();
const { dispatch } = useStore();
const { COMPANY_KEYS } = useContractTemplateKeys();

const emits = defineEmits<{
   (e: 'update:calculatedValue', payload: ContractTemplateCalculatedValue): void;
   (e: 'update:salaryTypeCalculatedValue', payload: ContractTemplateCalculatedValue): void;
   (e: 'onRemoveCalculatedValue', calculatedValueIndex: number): void;
   (e: 'showIndexationModal', salaryIndexationLogs: Array<SalaryIndexationLog>): void;
}>();
const props = defineProps({
   loading: { type: Boolean, default: false },
   field: { type: Object as PropType<ContractTemplateField>, default: null },
   fieldName: { type: Object as PropType<ContractTemplateFieldName>, default: null },
   errors: { type: Object, default: () => ({}) },
   calculatedValue: { type: Object as PropType<ContractTemplateCalculatedValue>, default: null },
   salaryTypeCalculatedValue: { type: Object as PropType<ContractTemplateCalculatedValue>, default: null },
   calculatedValueIndex: { type: Number, default: 0 },
   conditionTypes: { type: Array as PropType<Array<CalculatedFieldConditionType>>, default: () => [] },
});

const { fieldName, calculatedValue } = toRefs(props);

//#region Calculated Value
const options = ref<Array<any>>([]);

const selectedValue = computed(() => calculatedValue.value?.selected_value);

watch(selectedValue, setSelectedValue);
function setSelectedValue(selectedValue: any) {
   if (options.value?.length) return;
   options.value = [selectedValue];
}

onMounted(async () => {
   if (selectedValue.value) {
      options.value = [selectedValue.value];
   }
   if (fieldName.value?.form_type === FormType.Dropdown) {
      const isCompanyKey = COMPANY_KEYS.includes(fieldName.value.key);

      if (!isCompanyKey && fieldName.value.optionsUrl) {
         const response = await dispatch(fieldName.value.optionsUrl);
         options.value = Array.isArray(response) ? response : (response?.data ?? []);
         return;
      }
   }
   if (fieldName.value.options?.length) {
      return (options.value = fieldName.value.options);
   }
});

function setCalculatedValue(option: any) {
   if ([FormType.Dropdown, FormType.DropdownSearch].includes(fieldName.value.form_type)) {
      return emits('update:calculatedValue', { ...calculatedValue.value, value: option?.id, selected_value: option });
   }

   return emits('update:calculatedValue', { ...calculatedValue.value, value: option });
}

function setSalaryTypeCalculatedValue(option: any) {
   return emits('update:salaryTypeCalculatedValue', { ...props.salaryTypeCalculatedValue, value: option?.id, selected_value: option });
}
//#endregion

function setConditionType(conditionIndex: number, conditionType: CalculatedFieldConditionType) {
   const newCalculatedValue = { ...calculatedValue.value };
   const condition = newCalculatedValue?.conditions?.[conditionIndex];
   newCalculatedValue.conditions.splice(conditionIndex, 1, {
      ...condition,
      calculated_field_condition_type_id: conditionType?.id,
      calculated_field_condition_type: conditionType,
      value: null,
      min_value: null,
      max_value: null,
   });
}

function setConditionValue(condition: ContractTemplateCondition, option: any) {
   const formType = condition?.calculated_field_condition_type?.contract_template_field_type?.form_type;
   if (formType && [FormType.Dropdown, FormType.DropdownSearch].includes(formType)) {
      condition.value = option?.id;
      condition.selected_value = option;
      return;
   }

   return (condition.value = option);
}

//#region Condition Options
function getConditionOptions(condition: ContractTemplateCondition) {
   if (condition.calculated_field_condition_type?.options) {
      return condition.calculated_field_condition_type?.options;
   }
   if (condition.selected_value) {
      return [condition.selected_value];
   }
   return [];
}
//#endregion

//#region Salary Type
const salaryTypeFieldName = inject('salaryTypeFieldName', ref<ContractTemplateFieldName>());
//#endregion
</script>

<template>
   <div :class="['contract-template-calculated-value']">
      <!-- Calclated Value (Value) -->
      <div class="d-flex gap-2 align-items-center">
         <FormElement
            style="min-width: 23.08rem"
            :model-value="calculatedValue.value"
            :label="fieldName.label"
            :disabled="loading"
            :errors="errors"
            :form-type="fieldName.form_type"
            :name="`${fieldName.key}_${calculatedValueIndex}_value`"
            :display-key="fieldName.display_key"
            :options="options"
            :options-url="fieldName.optionsUrl"
            :mode="'light'"
            :required="true"
            :emit-object="true"
            @update:model-value="setCalculatedValue"
         />

         <FormElement
            v-if="fieldName.key === 'salary' && salaryTypeFieldName"
            style="min-width: 23.08rem"
            :model-value="salaryTypeCalculatedValue?.value"
            :label="salaryTypeFieldName.label"
            :disabled="loading"
            :errors="errors"
            :form-type="salaryTypeFieldName.form_type"
            :name="`${salaryTypeFieldName.key}_${calculatedValueIndex}_value`"
            :display-key="salaryTypeFieldName.display_key"
            :options="$store.state.salaryType?.salaryTypes || []"
            :mode="'light'"
            :required="true"
            :emit-object="true"
            @update:model-value="setSalaryTypeCalculatedValue"
         />

         <div v-if="calculatedValue.salary_indexation_logs?.length" class="mt-4 ms-3">
            <NxtLink @on-click="emits('showIndexationModal', calculatedValue.salary_indexation_logs)">
               {{ $t('actions.show-indexation-history') }}
            </NxtLink>
         </div>
      </div>

      <!-- Delete Calculated Value -->
      <div style="position: absolute; top: var(--nxt-gutter-small); right: var(--nxt-gutter-small); width: 3rem">
         <NxtIconButton
            :name="'trash'"
            :hover-text="$t('actions.delete-type', { type: $t('companies.templates.calculated-value') })"
            :hover-color="'var(--nxt-grey-200)'"
            @on-click="emits('onRemoveCalculatedValue', calculatedValueIndex)"
         />
      </div>

      <!-- Conditions -->
      <div class="contract-template-calculated-value__conditions">
         <!-- Condition -->
         <div
            v-for="(condition, conditionIndex) in calculatedValue.conditions"
            :key="conditionIndex"
            :class="['contract-template-calculated-value__condition']"
         >
            <strong v-if="conditionIndex === 0" style="margin-top: 2.4rem" class="px-2">
               {{ t('generic.if') }}
            </strong>
            <strong v-else style="margin-top: 2.4rem" class="px-2">
               {{ t('generic.and') }}
            </strong>

            <!-- Condition Type -->
            <div>
               <Dropdown
                  :model-value="condition?.calculated_field_condition_type_id"
                  :label="`${t('generic.type')} ${t('generic.condition')?.toLowerCase()}`"
                  :disabled="loading"
                  :errors="errors"
                  :name="`calculated_value_${calculatedValueIndex}_condition_type_${conditionIndex}`"
                  :options="conditionTypes"
                  :mode="'light'"
                  :emit-object="true"
                  :required="true"
                  @update:model-value="setConditionType(conditionIndex, $event)"
               />
            </div>

            <strong style="margin-top: 2.4rem" class="px-2">=</strong>

            <div>
               <!-- Age Range -->
               <div
                  v-if="condition.calculated_field_condition_type?.contract_template_field_type?.form_type === 'number_range'"
                  class="contract-template-calculated-value__number-range"
               >
                  <NxtInput
                     v-model="condition.min_value"
                     style="width: 50%"
                     :name="`calculated_value_${calculatedValueIndex}_value_min_${conditionIndex}`"
                     :mode="'light'"
                     :label="t('generic.min')"
                     :required="true"
                  />
                  <NxtInput
                     v-model="condition.max_value"
                     style="width: 50%"
                     :name="`calculated_value_${calculatedValueIndex}_value_max_${conditionIndex}`"
                     :required="true"
                     :mode="'light'"
                     :label="t('generic.max')"
                  />
               </div>

               <!-- Dropdown -->
               <FormElement
                  v-else-if="condition.calculated_field_condition_type_id"
                  :model-value="condition.value"
                  :label="condition.calculated_field_condition_type?.name"
                  :disabled="loading"
                  :errors="errors"
                  :form-type="condition.calculated_field_condition_type?.contract_template_field_type?.form_type"
                  :name="`calculated_value_${calculatedValueIndex}_condition_type_${conditionIndex}`"
                  :options="getConditionOptions(condition)"
                  :options-url="condition.calculated_field_condition_type?.optionsUrl"
                  :display-key="condition.calculated_field_condition_type?.display_key || 'name'"
                  :required="true"
                  :mode="'light'"
                  :emit-object="true"
                  @update:model-value="setConditionValue(condition, $event)"
               />
            </div>

            <div v-if="conditionIndex !== 0" class="d-flex align-items-center justify-content-center">
               <NxtIconButton
                  :style="{ marginTop: '1.8rem', width: ' 3rem' }"
                  :name="'trash'"
                  :hover-text="$t('actions.delete-type', { type: $t('generic.condition') })"
                  @on-click="calculatedValue?.conditions?.splice(conditionIndex, 1)"
               />
            </div>
         </div>

         <!-- Add Condition -->
         <NxtLink style="margin-left: 3.4rem" @on-click="calculatedValue?.conditions?.push({} as ContractTemplateCondition)">
            + {{ t('actions.add-type', { name: t('companies.templates.condition', 1) })?.toLowerCase() }}
         </NxtLink>
      </div>
   </div>
</template>

<style lang="scss" scoped>
.contract-template-calculated-value {
   display: grid;
   grid-template-columns: auto toRem(30);
   grid-template-rows: auto auto;
   gap: var(--nxt-gutter);

   width: 100%;
   background-color: var(--surface-base-0);
   padding: var(--nxt-gutter);
   position: relative;

   > * {
      &:nth-child(1) {
         grid-area: 1 / 1 / 2 / 2;
      }
      &:nth-child(2) {
         grid-area: 1 / 2 / 2 / 3;
      }
      &:nth-child(3) {
         grid-area: 2 / 1 / 3 / 3;
      }
   }
}

.contract-template-calculated-value__conditions {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   align-items: flex-start;
   width: 100%;

   padding-left: 5rem;
}

.contract-template-calculated-value__condition {
   display: grid;
   grid-template-columns: toRem(30) 1fr auto 1fr toRem(20);
   gap: var(--nxt-gutter);
   width: 100%;
}

.contract-template-calculated-value__number-range {
   display: flex;
   flex-direction: row;
   gap: var(--nxt-gutter-small);
   width: 100%;
}
</style>
